import { useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { Button } from '@/components/Button';
import { LuFolder, LuFileText, LuX, LuLoaderCircle, LuCircleCheckBig } from 'react-icons/lu';
import useParseCandidateDocument from '../api/useParseCandidateDocument';
import { showErrorToast } from '@/features/agent/job_change_processes/lib/toast';
import type { AgentApiCandidateProfilesParseDocument } from '@/types/serializers';

type Props = {
  /** 解析成功時に呼ばれる。解析結果と、アップロード対象ファイル名を受け取る */
  onParsed: (result: AgentApiCandidateProfilesParseDocument, fileName: string) => void;
  /** リセット時に呼ばれる。親が保持する blob_signed_id 等の状態をクリアするため */
  onCleared?: () => void;
};

/** 対応ファイル形式 */
const ACCEPTED_MIME_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const ACCEPTED_EXTENSIONS = ['.pdf', '.docx'];
const ACCEPT_ATTR =
  '.pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/**
 * 履歴書・職務経歴書アップロードと解析セクション。
 * ファイルを選択または D&D すると解析APIを呼び出し、結果を親に渡す。
 */
export default function ResumeUploadSection({ onParsed, onCleared }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const { mutate: parseDocument, isPending } = useParseCandidateDocument();

  /** ファイル形式バリデーション */
  const isValidFile = (file: File): boolean => {
    if (ACCEPTED_MIME_TYPES.includes(file.type)) return true;
    const lower = file.name.toLowerCase();
    return ACCEPTED_EXTENSIONS.some((ext) => lower.endsWith(ext));
  };

  /** 解析APIを呼び出す */
  const handleFile = (file: File) => {
    if (!isValidFile(file)) {
      showErrorToast('PDF または Word(.docx) ファイルを選択してください');
      return;
    }
    setUploadedFileName(file.name);
    parseDocument(file, {
      onSuccess: (result) => {
        onParsed(result, file.name);
      },
      onError: (error) => {
        setUploadedFileName(null);
        showErrorToast(error instanceof Error ? error.message : '書類の解析に失敗しました');
      },
    });
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleFile(file);
    // 同じファイルを再選択できるように value をリセット
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    handleFile(file);
  };

  /** ファイルをリセットして再選択できる状態に戻す */
  const handleClearFile = () => {
    setUploadedFileName(null);
    // 親が保持している解析済み blob_signed_id 等の状態もクリアする
    onCleared?.();
  };

  return (
    <div className="rounded border border-solid border-primary/30 bg-blue-50/50 p-4">
      <div className="mb-3 flex items-center gap-2">
        <LuFileText size={18} className="text-primary" />
        <h3 className="text-sm font-bold text-primary">書類から自動入力</h3>
        <span className="ml-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
          AI 解析
        </span>
      </div>

      {/* 解析済み状態 */}
      {uploadedFileName && !isPending ? (
        <div className="flex items-center gap-3 rounded border border-solid border-green-200 bg-green-50 px-4 py-3">
          <LuCircleCheckBig size={18} className="shrink-0 text-green-600" />
          <span className="min-w-0 flex-1 truncate text-sm text-green-800">
            {uploadedFileName} を解析しました
          </span>
          <button
            type="button"
            onClick={handleClearFile}
            className="ml-auto shrink-0 rounded p-1 hover:bg-green-100"
            aria-label="解析ファイルをリセット"
          >
            <LuX size={16} className="text-green-700" />
          </button>
        </div>
      ) : (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            className="hidden"
            onChange={handleInputChange}
            disabled={isPending}
            aria-label="書類ファイルを選択"
          />

          {/* ドラッグ&ドロップエリア */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={[
              'flex flex-col items-center justify-center gap-3 rounded border-2 border-dashed bg-white py-6 transition-colors',
              isDragging ? 'border-primary bg-blue-50' : 'border-gray-300',
              isPending ? 'pointer-events-none opacity-50' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            {isPending ? (
              /* 解析中ローディング表示 */
              <div className="flex flex-col items-center gap-2">
                <LuLoaderCircle size={32} className="animate-spin text-primary" />
                <p className="text-sm text-gray-600">AI が書類を解析中です...</p>
              </div>
            ) : (
              <>
                <LuFileText size={32} className="text-gray-400" />
                <div className="flex flex-col items-center gap-1">
                  <p className="text-sm text-gray-700">ここにドラッグ&ドロップ、または</p>
                </div>
                <Button
                  type="button"
                  color="secondary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isPending}
                  className="gap-1"
                >
                  <LuFolder size={16} />
                  ファイルを選択
                </Button>
              </>
            )}
          </div>

          <p className="mt-2 text-xs text-gray-500">
            ※ PDF または Word(.docx) 形式、5MB 以内のファイルに対応しています。
          </p>
        </>
      )}
    </div>
  );
}
